import { html, css } from 'lit-element';
import { Chart } from 'chart.js';
import { BaseElement } from './BaseElement';
import toUTCLocaleDateString from '../toUTCLocaleDateString';
import { PatientProgressData } from '../PatientProgressData';
import { CPMSessionDay } from '../CPMSessionDaily';

Chart.platform.disableCSSInjection = true;

const _data = Symbol();
const _resizeHandler = Symbol();
const _chart = Symbol();

class CPMUsageChart extends BaseElement {
	constructor() {
		super();

		this[_resizeHandler] = () => {
			if(this.chart) {
				this.chart.resize();
				this.chart.render();
			}
		};
	}
	static get styles() {
		return [
			...BaseElement.styles,
			css`
				:host {
					overflow: hidden;
				}
				canvas {
					width: 100%;
					height: 100%;
				}
				p {
					text-align: center;
					border: var(--theme-border, black);
					border-radius: 0.25em;
					padding: 0.25em;
					margin: 0.25em;
				}
			`
		];
	}
	get canvas() {
		return this.shadowRoot.querySelector('canvas');
	}
	get chart() {
		return this[_chart];
	}
	get data() {
		return this[_data];
	}
	set data(v) {
		if(v !== null && !(v instanceof PatientProgressData))
			throw new TypeError('data must be an instance of PatientProgressData.');
		this[_data] = v;
		this.requestUpdate();
		setTimeout(() => this.drawChart(), 0);
	}
	firstUpdated() {
		super.firstUpdated();
		this.drawChart();
	}
	connectedCallback() {
		super.connectedCallback();

		this.window.addEventListener('resize', this[_resizeHandler]);
	}
	disconnectedCallback() {
		this.window.removeEventListener('resize', this[_resizeHandler]);

		super.disconnectedCallback();

	}
	drawChart() {
		if(!this.data || !this.data.lastDay)
			return;
		
		const cpmSessionDays = [...this.data.cpmSessionDaily];
		let allDays = [];
		let date = new Date(this.data.orderDetail.surgeryDate);
		while(date.getTime() <= this.data.lastDay) {
			const cpmSessionDay = cpmSessionDays.find(v => v.usageDate === date.getTime());
			allDays.push(cpmSessionDay || CPMSessionDay.fromObject({ usageDate: date }));
			date.setUTCDate(date.getUTCDate() + 1);
		}

		const datasets = [
			{
				label: 'Usage Minutes',
				data: allDays.map(v => v.totalUsageTime ? Math.round(v.totalUsageTime / 60) : null),
				backgroundColor: [
					'rgba(0, 0, 0, 0.2)'
				],
				borderColor: [
					'rgba(0, 0, 255, 1)'
				],
				borderWidth: 2,
				fill: 'origin',
				spanGaps: false,
				pointBorderColor: 'rgba(0, 0, 255, 1)'
			}
		];
		if(this.data.doctorKneeProtocol) {
			datasets.push({
				label: 'Usage Goal',
				data: allDays.map(() => this.data.doctorKneeProtocol.sessionDuration * this.data.doctorKneeProtocol.sessionsPerDay),
				backgroundColor: [
					'rgba(0, 0, 0, 0)'
				],
				borderColor: [
					'rgba(100, 200, 100, 1)'
				],
				borderWidth: 2,
				pointBorderColor: 'rgba(100, 200, 100, 1)',
				fill: false
			});
		}
		
		this[_chart] = new Chart(this.canvas, {
			type: 'line',
			data: {
				labels: allDays.map(v => toUTCLocaleDateString(new Date(v.usageDate))),
				datasets
			},
			options: {
				scales: {
					yAxes: [{
						ticks: {
							beginAtZero: true,
							fontSize: 16
						}
					}],
					xAxes: [
						{
							ticks: {
								fontSize: 16
							}
						}
					]
				},
				legend: {
					labels: {
						fontSize: 16
					}
				},
				hover: {
					animationDuration: 0
				},
				responsive: false,
				animation: {
					duration: 0
				}
			}
		});
	}
	render() {
		const showCanvas = this.data && this.data.cpmSessionDaily.lastDay;
		return html`
			<canvas class="${showCanvas ? '' : 'no-display'}"></canvas>
			<p class="${!showCanvas ? '' : 'no-display'}">
				No CPM session data available.
			</p>
		`;
	}
}

customElements.define('pp-cpm-usage-chart', CPMUsageChart);